import { doc, getDoc, getDocFromServer, onSnapshot, setDoc } from "firebase/firestore";
import type { CycleAlignment } from "@/lib/cycleConfig";
/* eslint-disable @typescript-eslint/no-explicit-any, no-empty */
import { db, getAuthInstance, peekAuthInstance } from "@/lib/firebase";

const getDb = () => db;

export type RingRelationship = {
  id: string;
  parentId?: string | null;
  stagesPerParent?: number | null;
  // Preserve per-ring waveform settings so relationships round-trip UI selections (e.g., sine)
  waveform?: '24hr' | 'default' | 'sine' | 'triangle' | 'square' | 'sawtooth';
  waveformIntervalMultiplier?: number | null;
  waveformIntervalDivider?: number | null;
};

export type RelationshipData = {
  alignments: CycleAlignment[];
  ringRelationships: RingRelationship[];
  updatedAt?: string;
};

const RELATIONSHIP_DOC_PREFIX = "relationships-";
const LOCAL_RELATIONSHIP_KEY_PREFIX = "relationship-data:";

// Cache current user ID for per-user preference docs
let cachedUserId: string | null = null;

// Initialize user ID cache when auth loads
(async () => {
  try {
    const auth = await getAuthInstance();
    if (auth?.currentUser) {
      cachedUserId = auth.currentUser.uid;
    }
    const { onAuthStateChanged } = await import('firebase/auth');
    onAuthStateChanged(auth, (user) => {
      cachedUserId = user?.uid || null;
    });
  } catch {
    // Ignore auth errors; fallback to shared defaults
  }
})();

// Track whether we've shown the admin notification
let adminNotificationShown = false;

const showAdminNotification = () => {
  if (!adminNotificationShown) {
    adminNotificationShown = true;
    // This is just logging for now; the actual toast will be triggered from the component
    console.info('[relationshipStore] Admin editing global relationship settings that will affect all users');
  }
};

export const getRelationshipDocId = (viewId: string): string => `${RELATIONSHIP_DOC_PREFIX}${viewId}`;

// Resolve per-user doc (primary) and shared admin doc (fallback). User doc lives under dashboardState
// alongside per-user dashboard meta/layout, mirroring dashboardStore.
const resolveRelationshipDocIds = (baseId: string) => {
  // Allow callers to pass either raw view id or already-prefixed doc id.
  const suffix = baseId.startsWith(RELATIONSHIP_DOC_PREFIX)
    ? baseId.slice(RELATIONSHIP_DOC_PREFIX.length)
    : baseId;
  const liveUserId = cachedUserId ?? peekAuthInstance()?.currentUser?.uid ?? null;
  const userDocId = liveUserId ? `user-${liveUserId}` : null;
  const fallback = `${RELATIONSHIP_DOC_PREFIX}${suffix}`;
  const primary = userDocId ? `${RELATIONSHIP_DOC_PREFIX}${userDocId}-${suffix}` : fallback;
  return { primary, fallback, hasUser: Boolean(userDocId) };
};

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs = 4000): Promise<T> =>
  new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error("Firestore request timed out."));
    }, timeoutMs);

    promise
      .then((result) => {
        clearTimeout(timeoutId);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });

const getRelationshipSnapshot = async (relationshipDocId: string) => {
  const db = getDb();
  if (!db) throw new Error("Firestore is not configured.");
  const docRef = doc(db, "relationshipSets", relationshipDocId);
  try {
    return await withTimeout(getDocFromServer(docRef));
  } catch {
    return await withTimeout(getDoc(docRef));
  }
};

const emitIntent = (_name: string, _detail?: any) => {
  // diagnostics removed
};

const logFirestoreWrite = (detail: Record<string, unknown>) => {
  try {
    console.info('[firestore:write] relationshipStore', detail);
  } catch {
    // ignore logging errors
  }
};


const normalizeRelationships = (data: RelationshipData): RelationshipData => ({
  alignments: data.alignments ?? [],
  ringRelationships: (data.ringRelationships ?? []).map((relationship) => ({
    id: relationship.id,
    parentId: relationship.parentId ?? undefined,
    stagesPerParent: relationship.stagesPerParent ?? undefined,
    waveform: relationship.waveform,
    waveformIntervalMultiplier: relationship.waveformIntervalMultiplier ?? undefined,
    waveformIntervalDivider: relationship.waveformIntervalDivider ?? undefined,
  })),
  updatedAt: data.updatedAt,
});

const loadLocalRelationships = (storageKey: string): RelationshipData | null => {
  const stored = localStorage.getItem(`${LOCAL_RELATIONSHIP_KEY_PREFIX}${storageKey}`);
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored) as RelationshipData;
    return normalizeRelationships(parsed);
  } catch {
    return null;
  }
};

const saveLocalRelationships = (storageKey: string, data: RelationshipData) => {
  localStorage.setItem(`${LOCAL_RELATIONSHIP_KEY_PREFIX}${storageKey}`, JSON.stringify(data));
};

// Track last saved relationship payload to prevent duplicate writes
const _lastSavedRelationshipPayload: Record<string, string> = {};
const _pendingRelationshipPayload: Record<string, string | undefined> = {};
let relationshipRemoteWritable = true;
let relationshipRemoteWarned = false;

const isPermissionError = (error: unknown): boolean => {
  const message = String(error ?? "").toLowerCase();
  return (
    message.includes("permission") ||
    message.includes("insufficient") ||
    message.includes("missing or insufficient permissions")
  );
};

export async function loadRelationshipData(relationshipDocId: string): Promise<RelationshipData | null> {
  const { primary, fallback } = resolveRelationshipDocIds(relationshipDocId);
  const localKey = primary;

  if (typeof window !== 'undefined' && window.localStorage?.getItem('disableFirestore') === '1') {
    try { emitIntent('loadRelationshipData_blocked_local', { relationshipDocId: primary }); } catch {}
    return loadLocalRelationships(localKey);
  }
  const db = getDb();
  if (!db) {
    return loadLocalRelationships(localKey);
  }

  const attemptLoad = async (docId: string) => {
    try {
      const snapshot = await getRelationshipSnapshot(docId);
      if (!snapshot.exists()) return null;
      return normalizeRelationships(snapshot.data() as RelationshipData);
    } catch (error) {
      if (isPermissionError(error)) {
        relationshipRemoteWritable = false;
        return null;
      }
      throw error;
    }
  };

  const primaryData = await attemptLoad(primary);
  if (primaryData) return primaryData;
  if (fallback && fallback !== primary) {
    const fallbackData = await attemptLoad(fallback);
    if (fallbackData) return fallbackData;
  }
  return loadLocalRelationships(localKey);
}

export async function saveRelationshipData(
  relationshipDocId: string,
  data: RelationshipData,
  isAdmin: boolean = false
): Promise<void> {
  const { primary, fallback } = resolveRelationshipDocIds(relationshipDocId);
  // Admins save to shared/global doc (fallback), non-admins save to personal doc (primary)
  const targetDocId = isAdmin ? fallback : primary;
  
  // Show one-time notification for admins
  if (isAdmin) {
    showAdminNotification();
  }

  const sanitized = {
    alignments: data.alignments.map((alignment) => ({
      sourceCycleId: alignment.sourceCycleId,
      targetCycleId: alignment.targetCycleId,
      sourceStage: alignment.sourceStage,
      targetStage: alignment.targetStage,
      name: alignment.name,
      ...(alignment.sourceSegments !== undefined
        ? { sourceSegments: alignment.sourceSegments }
        : {}),
      ...(alignment.targetSegments !== undefined
        ? { targetSegments: alignment.targetSegments }
        : {}),
      ...(alignment.controlMode !== undefined
        ? { controlMode: alignment.controlMode }
        : {}),
    })),
    ringRelationships: data.ringRelationships.map((relationship) => ({
      id: relationship.id,
      parentId: relationship.parentId ?? null,
      stagesPerParent: relationship.stagesPerParent ?? null,
      ...(relationship.waveform ? { waveform: relationship.waveform } : {}),
      ...(relationship.waveformIntervalMultiplier !== undefined
        ? { waveformIntervalMultiplier: relationship.waveformIntervalMultiplier }
        : {}),
      ...(relationship.waveformIntervalDivider !== undefined
        ? { waveformIntervalDivider: relationship.waveformIntervalDivider }
        : {}),
    })),
  };
  
  // Check if payload has actually changed to prevent write spam
  const currentPayload = JSON.stringify(sanitized);
  if (_lastSavedRelationshipPayload[targetDocId] === currentPayload) {
    try { emitIntent('saveRelationshipData_skipped_duplicate', { relationshipDocId: targetDocId }); } catch {}
    return; // Skip write - no actual changes
  }
  if (_pendingRelationshipPayload[targetDocId] === currentPayload) {
    try { emitIntent('saveRelationshipData_skipped_inflight', { relationshipDocId: targetDocId }); } catch {}
    return; // Skip write - identical payload is already being written
  }
  
  const db = getDb();
  if (!db || !relationshipRemoteWritable) {
    _pendingRelationshipPayload[targetDocId] = currentPayload;
    _lastSavedRelationshipPayload[targetDocId] = currentPayload;
    try {
      console.debug('[relationshipStore] saveLocalRelationships', targetDocId, sanitized);
    } catch {}
    saveLocalRelationships(targetDocId, sanitized);
    delete _pendingRelationshipPayload[targetDocId];
    return;
  }
  try {
    console.debug('[relationshipStore] saveRelationshipData -> firestore', targetDocId, sanitized);
  } catch {}
  
  try {
    _pendingRelationshipPayload[targetDocId] = currentPayload;
    logFirestoreWrite({ relationshipDocId: targetDocId, action: 'saveRelationship' });
    await setDoc(doc(db, "relationshipSets", targetDocId), sanitized, { merge: true });
    _lastSavedRelationshipPayload[targetDocId] = currentPayload;
    delete _pendingRelationshipPayload[targetDocId];
    try { emitIntent('saveRelationshipData_setdoc', { relationshipDocId: targetDocId }); } catch {}
  } catch (err) {
    delete _pendingRelationshipPayload[targetDocId];
    if (isPermissionError(err)) {
      relationshipRemoteWritable = false;
      _lastSavedRelationshipPayload[targetDocId] = currentPayload;
      saveLocalRelationships(targetDocId, sanitized);
      if (!relationshipRemoteWarned) {
        relationshipRemoteWarned = true;
        try {
          console.warn('[relationshipStore] Firestore denied, switched to local relationship persistence');
        } catch {}
      }
      return;
    }
    try { emitIntent('saveRelationshipData_error', { relationshipDocId: targetDocId, error: String(err) }); } catch {}
    throw err;
  }
}

export function subscribeRelationshipData(
  relationshipDocId: string,
  onData: (data: RelationshipData | null) => void,
  onError?: (error: Error) => void
): (() => void) | null {
  const { primary, fallback } = resolveRelationshipDocIds(relationshipDocId);
  const localKey = primary;

  if (typeof window !== 'undefined' && window.localStorage?.getItem('disableFirestore') === '1') {
    try { emitIntent('subscribeRelationshipData_blocked_local', { relationshipDocId: primary }); } catch {}
    onData(loadLocalRelationships(localKey));
    return null;
  }
  const db = getDb();
  if (!db || !relationshipRemoteWritable) {
    onData(loadLocalRelationships(localKey));
    return null;
  }

  const docRef = doc(db, "relationshipSets", primary);
  let hasEmittedFallback = false;

  const tryEmitFallback = async () => {
    if (!fallback || fallback === primary || hasEmittedFallback) return;
    try {
      const snap = await getRelationshipSnapshot(fallback);
      if (snap.exists()) {
        hasEmittedFallback = true;
        onData(normalizeRelationships(snap.data() as RelationshipData));
      }
    } catch (err) {
      onError?.(err as Error);
    }
  };

  const unsubscribe = onSnapshot(
    docRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        void tryEmitFallback();
        return;
      }
      const data = snapshot.data() as RelationshipData;
      onData(normalizeRelationships(data));
    },
    (error) => {
      if (isPermissionError(error)) {
        relationshipRemoteWritable = false;
        if (!relationshipRemoteWarned) {
          relationshipRemoteWarned = true;
          try {
            console.warn('[relationshipStore] Firestore denied, using local relationship data');
          } catch {}
        }
        onData(loadLocalRelationships(localKey));
        return;
      }
      onError?.(error as Error);
    }
  );

  void tryEmitFallback();
  return unsubscribe;
}
